import React, { useEffect, useRef, useState } from 'react';
import { Phone, MessageCircle, FileText } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import InvestorDeckModal from './InvestorDeckModal';

// PLEXTERRA mobile bottom action bar.
// Displays 3 equal-width tap targets pinned to the bottom edge on screens
// smaller than `md` (768px). Slides out of view when the reader scrolls
// down (long articles feel less crowded) and slides back in on scroll-up
// or when the user is within ~120px of the top / bottom of the page.
// Hidden on desktop.
const COPY = {
  fr: { call: 'Appeler', whatsapp: 'WhatsApp', deck: 'Deck' },
  en: { call: 'Call', whatsapp: 'WhatsApp', deck: 'Deck' },
};

const PHONE_E164 = '14385223302';
const WA_TEXT_FR = "Bonjour ! J'aimerais discuter d'un projet institutionnel.";
const WA_TEXT_EN = "Hello! I'd like to discuss an institutional project.";
const SCROLL_DELTA = 12;         // ignore jitter smaller than this (px)
const NEAR_EDGE_PX = 120;        // always show near top / bottom of page

const MobileActionBar = () => {
  const { language } = useLanguage();
  const t = COPY[language] || COPY.en;
  const [deckOpen, setDeckOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset || 0;
        const dy = y - lastY.current;
        const nearTop = y < NEAR_EDGE_PX;
        const nearBottom =
          window.innerHeight + y >= document.documentElement.scrollHeight - NEAR_EDGE_PX;

        if (nearTop || nearBottom) {
          setHidden(false);
        } else if (Math.abs(dy) > SCROLL_DELTA) {
          setHidden(dy > 0); // hide when scrolling down, show when scrolling up
        }
        lastY.current = y;
        ticking.current = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const waText = language === 'fr' ? WA_TEXT_FR : WA_TEXT_EN;
  const waUrl = `https://api.whatsapp.com/send?phone=${PHONE_E164}&text=${encodeURIComponent(waText)}`;

  return (
    <>
      <nav
        className={`md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0B1E3F] text-white border-t border-[#C6A15A]/40 shadow-[0_-8px_24px_rgba(11,30,63,0.25)] transition-transform duration-300 ease-out will-change-transform ${
          hidden ? 'translate-y-full' : 'translate-y-0'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="mobile-action-bar"
        data-hidden={hidden ? 'true' : 'false'}
        aria-label="Quick contact actions"
        aria-hidden={hidden}
      >
        <div className="grid grid-cols-3 divide-x divide-white/10">
          <a
            href={`tel:+${PHONE_E164}`}
            className="flex flex-col items-center justify-center gap-1 py-3 min-h-[56px] active:bg-white/10 transition"
            data-testid="mobile-nav-call"
            aria-label={t.call}
          >
            <Phone className="w-5 h-5 text-[#C6A15A]" strokeWidth={1.75} />
            <span className="text-[10px] tracking-[0.2em] uppercase font-medium">{t.call}</span>
          </a>

          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-1 py-3 min-h-[56px] active:bg-white/10 transition"
            data-testid="mobile-nav-whatsapp"
            aria-label={t.whatsapp}
          >
            <MessageCircle className="w-5 h-5 text-[#C6A15A]" strokeWidth={1.75} />
            <span className="text-[10px] tracking-[0.2em] uppercase font-medium">{t.whatsapp}</span>
          </a>

          <button
            type="button"
            onClick={() => setDeckOpen(true)}
            className="flex flex-col items-center justify-center gap-1 py-3 min-h-[56px] active:bg-white/10 transition"
            data-testid="mobile-nav-deck"
            aria-label={t.deck}
          >
            <FileText className="w-5 h-5 text-[#C6A15A]" strokeWidth={1.75} />
            <span className="text-[10px] tracking-[0.2em] uppercase font-medium">{t.deck}</span>
          </button>
        </div>
      </nav>

      <InvestorDeckModal open={deckOpen} onOpenChange={setDeckOpen} />
    </>
  );
};

export default MobileActionBar;
